import React, { useEffect, useState } from 'react';
import { carteraApi } from '../api';
import { C } from './estilos';

/**
 * "¿A quién le avisamos?" — el encargado que recibe los avisos del bot.
 *
 * A diferencia de los switches del modal (todavía vista previa), esto SÍ se
 * guarda: escribe `encargado_*` en bot_ventas_configs con el endpoint que ya
 * existe (POST /bot-ventas/config). Lo guardado aquí manda sobre
 * BOT_VENTAS_AVISO_TEL y BOT_VENTAS_AVISO_EMAIL_TO del env.
 *
 * 🔴 Teléfono y correo se mandan SIEMPRE explícitos. El backend resuelve
 * columna → env → usuario, así que elegir solo un admin dejaría ganar al env.
 * El selector de admin solo rellena el correo.
 *
 * Igual que BotVentasSwitch: sin estado optimista. Se pinta con lo que el
 * backend respondió.
 */
export const DestinatarioAvisos = () => {
  const [cargando, setCargando] = useState(true);
  const [admins, setAdmins] = useState([]);
  const [guardado, setGuardado] = useState(null);
  const [form, setForm] = useState({ userId: '', telefono: '', email: '' });
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState('');
  const [ok, setOk] = useState(false);

  useEffect(() => {
    const control = new AbortController();
    carteraApi.getBotVentasConfig(control.signal)
      .then(({ data }) => {
        setAdmins(Array.isArray(data?.encargados_posibles) ? data.encargados_posibles : []);
        pintar(data?.config, setGuardado, setForm);
      })
      .catch((e) => {
        if (control.signal.aborted) return;
        setError(mensajeDeError(e, 'No se pudo leer a quién se avisa.'));
      })
      .finally(() => {
        if (!control.signal.aborted) setCargando(false);
      });
    return () => control.abort();
  }, []);

  const cambiar = (campo) => (event) => {
    setOk(false);
    setForm((f) => ({ ...f, [campo]: event.target.value }));
  };

  const elegirAdmin = (event) => {
    const id = event.target.value;
    const admin = admins.find((a) => String(a.id) === id);
    setOk(false);
    setForm((f) => ({ ...f, userId: id, email: admin ? admin.email : f.email }));
  };

  const guardar = async () => {
    setGuardando(true);
    setError('');
    setOk(false);
    try {
      const { data } = await carteraApi.actualizarBotVentasEncargado({
        encargado_user_id: form.userId ? Number(form.userId) : null,
        encargado_telefono: form.telefono.trim() || null,
        encargado_email: form.email.trim() || null,
      });
      pintar(data?.config, setGuardado, setForm);
      setOk(true);
    } catch (e) {
      setError(mensajeDeError(e, 'No se pudo guardar. Intenta de nuevo.'));
    } finally {
      setGuardando(false);
    }
  };

  const sinCambios = guardado !== null
    && form.userId === guardado.userId
    && form.telefono.trim() === guardado.telefono
    && form.email.trim() === guardado.email;

  return (
    <div aria-label="A quién le avisamos" role="group" style={ESTILO.caja}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 14.5, fontWeight: 700, color: C.ink }}>¿A quién le avisamos?</span>
        <span style={ESTILO.chipGuarda}>
          <i className="pi pi-check-circle" style={{ fontSize: 11 }} />esto sí se guarda
        </span>
      </div>
      <p style={{ margin: '4px 0 10px', fontSize: 12.5, color: C.ink3, lineHeight: 1.45 }}>
        A esta persona le llegan los avisos del bot: cotización aceptada, lead que pide asesor y expediente completo.
        Lo que guardes aquí manda sobre el teléfono y el correo del servidor.
      </p>

      {cargando ? (
        <div style={{ fontSize: 12.5, color: C.gris }}>
          <span className="pi pi-spin pi-spinner" style={{ fontSize: 11, marginRight: 6 }} />Cargando…
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
          <label style={ESTILO.etiqueta}>
            Admin de PEICAR (opcional, rellena el correo)
            <select value={form.userId} onChange={elegirAdmin} disabled={guardando} style={ESTILO.campo}>
              <option value="">Sin admin</option>
              {admins.map((a) => (
                <option key={a.id} value={String(a.id)}>{a.name} · {a.email}</option>
              ))}
            </select>
          </label>
          <label style={ESTILO.etiqueta}>
            WhatsApp (10 dígitos)
            <input value={form.telefono} onChange={cambiar('telefono')} disabled={guardando} inputMode="tel" placeholder="4421234567" style={ESTILO.campo} />
          </label>
          <label style={ESTILO.etiqueta}>
            Correo
            <input value={form.email} onChange={cambiar('email')} disabled={guardando} type="email" placeholder="asesor@peicar.com.mx" style={ESTILO.campo} />
          </label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <button type="button" onClick={guardar} disabled={guardando || sinCambios} style={{ ...ESTILO.boton, opacity: guardando || sinCambios ? 0.55 : 1 }}>
              {guardando ? 'Guardando…' : 'Guardar'}
            </button>
            {ok && <span role="status" style={{ fontSize: 12.5, color: C.verde, fontWeight: 600 }}>Guardado</span>}
          </div>
        </div>
      )}

      {error && (
        <div role="alert" style={{ marginTop: 8, fontSize: 12, color: '#B42318', lineHeight: 1.35 }}>{error}</div>
      )}
    </div>
  );
};

const pintar = (config, setGuardado, setForm) => {
  const valores = {
    userId: config?.encargado_user_id ? String(config.encargado_user_id) : '',
    telefono: config?.encargado_telefono ? String(config.encargado_telefono) : '',
    email: config?.encargado_email ? String(config.encargado_email) : '',
  };
  setGuardado(valores);
  setForm(valores);
};

const mensajeDeError = (error, porDefecto) => {
  const respuesta = error?.response;
  if (respuesta?.data?.message) return respuesta.data.message;
  if (!respuesta) return 'No hay conexión con el servidor. Revisa tu red y vuelve a intentar.';
  return porDefecto;
};

const ESTILO = {
  caja: {
    margin: '6px 4px 12px', padding: '12px 14px', borderRadius: 12,
    background: C.fondo, border: `1px solid ${C.lineaSuave}`,
  },
  chipGuarda: {
    fontSize: 11, color: C.verde, border: `1px solid ${C.verde}`, borderRadius: 10,
    padding: '0 7px', display: 'inline-flex', gap: 4, alignItems: 'center', lineHeight: '18px',
  },
  etiqueta: { display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, fontWeight: 600, color: C.ink2 },
  campo: {
    border: `1px solid ${C.linea}`, borderRadius: 9, padding: '8px 10px', fontSize: 13,
    color: C.ink, background: '#fff', outline: 'none',
  },
  boton: {
    border: 'none', borderRadius: 9, padding: '8px 16px', cursor: 'pointer',
    background: C.steel, color: '#fff', fontSize: 12.5, fontWeight: 700,
  },
};

export default DestinatarioAvisos;
